<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">
    <h1 class="questions__header">{{ $t("Contracts") }}</h1>
    <p>{{
        $t("The contact details are necessary on further pages. Therefore go through all the steps, after which it is possible to save the page.")
      }}</p>

    <div v-show="displayStep >= 1">
      <h2 class="questions__subheader questions__header">{{ $t("Contract types") }}</h2>
      <h2 class="questions__subheader">{{
          $t("Below there is a list of the contract types which are available by default in Dyflexis. Please indicate which ones apply:")
        }}</h2>
      <div v-for="(contractType) in contract.contractTypes">
        <b-form-checkbox
          v-model="contractType.active"
          :disabled="readOnly"
          @click="updateOvertimeValues()"
          name="check-button"
          switch
          @change="nextStep(2)">
          {{ contractType.displayName }}
        </b-form-checkbox>
      </div>
    </div>

    <div v-show="displayStep >= 1 && externalContractTypeEnabled">
      <h2 class="questions__subheader">
        {{ $t("In case you work with several employment agencies: enter the names of the payroll agencies.") }}</h2>
      <input v-model="contract.multiplePayrollCompanies" class="form-control" type="text">
    </div>

    <h2 class="questions__subheader">{{
        $t("Do you use contract types that cannot be incorporated into the above list?Please add them below, they will be discussed in more detail during the intake.")
      }}</h2>
    <input v-model="nextContractTypeName" class="form-control" type="text">
    <b-button
      class="question--marginTop"
      variant="success"
      @click="addContractType">
      + {{ $t("Add contract type") }}
    </b-button>

    <div v-show="displayStep >= 2">
      <h2 class="questions__subheader questions__header">{{ $t("Contract Basis") }}</h2>
      <h2 class="questions__subheader">{{
          $t("In Dyflexis we have, in principle, two types of contracts: fixed and variable. Employees with a fixed number of hours in their contract have a base. The term 'Fixed' says nothing about the duration of the contract, but about the contract hours that are paid out by default.")
        }}</h2>
      <h2 class="questions__subheader">{{ $t("For each contract type, indicate whether it has a fixed base:") }}</h2>
      <form class="question--marginTop">
        <tr v-for="(contractType) in contract.contractTypes" v-if="contractType.active&&(contractType.displayName===$t('Intern')||contractType.displayName===$t('Fixed'))">
          <td class="question__overtimeSmall"><b>{{ contractType.displayName }}</b></td>
          <td>
            <b-form-checkbox
              v-model="contractType.contractType"
              :disabled="readOnly"
              class="checkbox"
              name="check-button"
              switch>
              {{ $t("Yes, this contract type has a fixed base") }}
            </b-form-checkbox>
          </td>
        </tr>
      </form>

      <h2 class="questions__subheader questions__header">{{ $t("Remuneration timeframe") }}</h2>
      <h2 class="questions__subheader">{{
          $t("The pay period specifies which days are included in a remuneration.")
        }}</h2>

      <h2 class="questions__subheader">{{
          $t("You can decide to have a short pay timeframe of one week in which employee balances are updated more frequently and the control over the hours covers a short timeframe. It is possible to send multiple weeks from Dyflexis to a timeframe in the payroll software. In addition, it is possible to choose all full weeks in a month, which results in recurring timeframe of 4 weeks, 4 weeks and 5 weeks; this sequence repeats 4 times a year. If there are allowance rules on a weekly basis, then you must choose a pay timeframe of whole weeks. If you choose a monthly timeframe, then the calendar month is used.")
        }}</h2>

      <h2 class="questions__subheader">{{
          $t("The starting day in the remuneration timeframe determines on which day the timeframe starts. Depending on which 'timeframe type' is chosen, there are other possibilities.")
        }}</h2>

      <ul>
        <li>{{
            $t("Week. If week is selected, it can be specified that the week starts on Monday, for example, or on the previous Tuesday, for example (to control what 'week 1' is in a new year).")
          }}
        </li>
        <li>{{
            $t("Month. If month is selected, the 1st of the month or the 16th before or after can be specified, for example (again, to control what is 'month 1' in a new year).")
          }}
        </li>
        <li>{{
            $t("4-weekly. With 4-weekly, the Monday can be chosen as the starting day or a number of days before or after (again, to control what in this case is 'period 1' in a new year).")
          }}
        </li>
        <li>{{
            $t("Manual date. With the manual date option, the start day is always the first day of the self-selected period.")
          }}
        </li>
      </ul>

      <h2 class="questions__subheader">{{
          $t("When a remuneration timeframe is over, you lock the hours so that the balances are updated and the entries cannot be modified. Then you can send the hours for remuneration.")
        }}</h2>

      <form class="question--marginTop">
        <tr>
          <th class="questions__contracttype"></th>
          <th>{{ $t("Weekly") }}</th>
          <th>{{ $t("4-weekly") }}</th>
          <th>{{ $t("Monthly") }}</th>
          <th>{{ $t("4, 4, 5") }}</th>
          <th>{{ $t("Starting day") }}</th>
        </tr>
        <tr v-for="(contractType) in contract.contractTypes"
            v-if="contractType.active"
            class="questions__trDropdown"
            @click="nextStep(3)">
          <td>{{ contractType.displayName }}</td>
          <td>
            <b-form-radio v-model="contractType.payPeriod" :disabled="readOnly" value="weekly"></b-form-radio>
          </td>
          <td>
            <b-form-radio v-model="contractType.payPeriod" :disabled="readOnly" value="4-weekly"></b-form-radio>
          </td>
          <td>
            <b-form-radio v-model="contractType.payPeriod" :disabled="readOnly" value="monthly"></b-form-radio>
          </td>
          <td>
            <b-form-radio v-model="contractType.payPeriod" :disabled="readOnly" value="445"></b-form-radio>
          </td>
          <td>
            <b-form-select v-show="contractType.payPeriod==='weekly'" v-model="contractType.startDayPayPeriod"
                           :disabled="readOnly" :options="weekOptions"></b-form-select>
            <b-form-select v-show="contractType.payPeriod==='monthly'" v-model="contractType.startDayPayPeriod"
                           :disabled="readOnly" :options="monthOptions"></b-form-select>
            <input
              v-show="contractType.payPeriod==='4-weekly' || contractType.payPeriod==='445'"
              v-model="contractType.startDayPayPeriod"
              :disabled="readOnly"
              class="form-control"
              type="date"
            />
          </td>
        </tr>
      </form>
    </div>

    <div v-show="displayStep >= 3 && fixedContractBase">
      <h2 class="questions__subheader questions__header">{{ $t("Contractbase") }}</h2>
      <h2 class="questions__subheader"><a :href="payrollPeriodUrl"
                                          target="_blank">{{
          $t("The base is the number of contract hours an employee is required to work during the remuneration timeframe. When more or less hours are worked than the base, they are characterized as plus/minus hours. Only fixed contract types have a base. There are various ways of calculating this, click here for more information.")
        }}</a></h2>

      <p class="questions__subheader questions--noinputmargin"><b>{{ $t("Average:") }}</b></p>
      <p class="questions--noinputmargin">-
        {{ $t("For weekly and 4-weekly remuneration timeframe, the periods are equal to the number of hours") }}<br>
        - {{
          $t("With a monthly remuneration timeframe, we divide the year into 12 equal periods: contract hours * 52 weeks / 12 months")
        }}<br>
        - {{
          $t("If an employee is employed for a portion of a remuneration timeframe, we multiply the base by the percentage of the period for which the employee is employed")
        }}</p>

      <p class="questions__subheader questions--noinputmargin"><b>{{ $t("Workable days:") }}</b></p>
      <p class="questions--noinputmargin">
        {{ $t("Contract hours / 5 * the number of workable days (Monday through Friday) in the period") }}</p>
      <p class="questions--noinputmargin">-
        {{ $t("If you have a monthly remuneration timeframe, keep in mind that the base varies") }}<br>
        - {{ $t("If you have a weekly pay period then each period is equal to the number of workable days") }}<br>
        - {{
          $t("If an employee is employed for a portion of a remuneration timeframe, we count the number of workable days during that period")
        }}</p>

      <form class="question--marginTop">
        <tr>
          <th class="questions__contracttype"></th>
          <th>{{ $t("Average:") }}</th>
          <th>{{ $t("Workable days:") }}</th>
        </tr>
        <tr v-for="(contractType) in contract.contractTypes"
            v-if="contractType.active&&contractType.contractType">
          <td>{{ contractType.displayName }}</td>
          <td>
            <b-form-radio v-model="contractType.calculateBy" :disabled="readOnly" value="montlyAverage"
                          @change="nextStep(4)"></b-form-radio>
          </td>
          <td>
            <b-form-radio v-model="contractType.calculateBy" :disabled="readOnly" value="workableDays"
                          @change="nextStep(4)"></b-form-radio>
          </td>
        </tr>
      </form>

      <h2 class="questions__subheader">{{ $t("How many hours is a full-time work week?") }}</h2>
      <input
        v-model="contract.fulltimeHours"
        class="form-control"
        min="0"
        type="number"
        @click="nextStep(4)"
      />
      <h2 class="questions__subheader">{{
          $t("If you are using multiple full-time hours, please enter them below")
        }}</h2>
      <input
        v-model="contract.fulltimeHoursOther"
        class="form-control questions--smallinput"
        type="text"
      />
      <h2 class="questions__subheader">{{
          $t("Annual hours system means that a number of hours per year are agreed with an employee that he/she must work. In the case of an annual hours system, the pay period will usually be a month. The hours to be worked each month are calculated based on the annual hours.")
        }}</h2>

      <b-form-checkbox
        v-model="jusEnabled"
        name="check-button"
        switch
        @change="nextStep(4); contract.jusDescription=null;">
        {{ $t("We use annual hours system") }}
      </b-form-checkbox>

      <div v-show="jusEnabled">
        <h2 class="questions__subheader">{{ $t("Enter the amount of hours per year") }}</h2>
        <input
          v-model="contract.jusDescription"
          class="form-control"
          max="100"
          min="0"
          type="number"
          @change="nextStep(4)"
        />
      </div>

      <h2 class="questions__subheader">{{
          $t("An employee with a min-max contract has an agreement that specifies both a minimum and a maximum number of hours. For this minimum number of hours the employee must always be offered work, and the employee will also always The hours between minimum and maximum are the 'flexible space', these may be planned in, but are not required. Within Dyflexis any hours above min and up to max are paid as overtime (they also count towards vacation entitlement). Any hours above max are saved in the TFT balance.")
        }}</h2>

      <b-form-checkbox
        v-model="minMaxEnabled"
        name="check-button"
        switch>
        {{ $t("We use min-max contracts") }}
      </b-form-checkbox>

      <div v-show="minMaxEnabled">
        <h2 class="questions__subheader">{{ $t("Explanation min-max") }}</h2>
        <b-form-textarea
          v-model="contract.minMaxDescription"
          class="questions__textarea"
          rows="5"></b-form-textarea>
      </div>

    </div>

    <div v-show="displayStep >= 4">
      <h1 class="questions__header question--margintop">{{ $t("Comments and save") }}</h1>
      <h2 class="questions__subheader">{{ $t("Comments on contracts") }}</h2>
      <b-form-textarea
        v-model="contract.remarks"
        class="questions__textarea"
        rows="3"/>

      <input :value="$t('Save')" type="button" @click="handleSave"/>
      <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
    </div>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "../GlobalFunctions";

export default {
  name: "Contracts",
  data() {
    return {
      displayFrontend: false,
      safeFreeze: false,
      showSaveStatus: false,
      jusEnabled: false,
      minMaxEnabled: false,
      tftPayout: false,
      nextContractTypeName: null,
      externalContractTypeEnabled: false,
      payrollPeriodUrl: 'https://www.dyflexis.com/wp-content/uploads/2019/04/payrollPeriod_EN.png',
      displayStep: 1,
      weekdays: [
        {value: 'monday', text: this.$t("Monday")},
        {value: 'tuesday', text: this.$t("Tuesday")},
        {value: 'wednesday', text: this.$t("Wednesday")},
        {value: 'thursday', text: this.$t("Thursday")},
        {value: 'friday', text: this.$t("Friday")},
        {value: 'saturday', text: this.$t("Saturday")},
        {value: 'sunday', text: this.$t("Sunday")},
      ],
      weekOptions: [
        {value: 'tuesday-before', text: this.$t("Tuesday") + ' ' + this.$t("before")},
        {value: 'wednesday-before', text: this.$t("Wednesday") + ' ' + this.$t("before")},
        {value: 'thursday-before', text: this.$t("Thursday") + ' ' + this.$t("before")},
        {value: 'friday-before', text: this.$t("Friday") + ' ' + this.$t("before")},
        {value: 'saturday-before', text: this.$t("Saturday") + ' ' + this.$t("before")},
        {value: 'sunday-before', text: this.$t("Sunday") + ' ' + this.$t("before")},
        {value: 'monday', text: this.$t("Monday")},
        {value: 'tuesday-after', text: this.$t("Tuesday") + ' ' + this.$t("after")},
        {value: 'wednesday-after', text: this.$t("Wednesday") + ' ' + this.$t("after")},
        {value: 'thursday-after', text: this.$t("Thursday") + ' ' + this.$t("after")},
        {value: 'friday-after', text: this.$t("Friday") + ' ' + this.$t("after")},
        {value: 'saturday-after', text: this.$t("Saturday") + ' ' + this.$t("after")},
        {value: 'sunday-after', text: this.$t("Sunday") + ' ' + this.$t("after")},
      ],
      contractTypesOptions: [
        {value: 'allFixedBase', text: this.$t("All contracts with a fixed base")},
        {value: 'allVariableBase', text: this.$t("All contracts with a variable base")},
      ],
      monthOptions: [],
      contract: {
        startDateMonthlyPaymentPeriod: null,
        overtimePay: null,
        overtimePayNotes: null,
        tftPayoutThreshold: null,
        overtimePayOther: null,
        overtime: null,
        jusDescription: null,
        fulltimeHours: null,
        fulltimeHoursOther: null,
        keepAnnualBalance: false,
        keepAnnualBalanceLawfull: false,
        holidayHours: null,
        holidayPercentage: null,
        annualLeaveOnAdditionalHours: false,
        minMaxDescription: null,
        deviatingPayPeriod: null,
        enableAnnualBalance: false,
        multiplePayrollCompanies: null,
        keepStatutoryAndNonstatutoryLeave: false,
        amountShortTimeWorkingHours: null,
        leaveRemarks: null,
        remarks: null,
        contractTypes: [{
          name: "Fixed",
          displayName: this.$t("Fixed"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          name: "Variable",
          displayName: this.$t("Variable"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          display: "Payroll",
          displayName: this.$t("Payroll"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          name: "Intern",
          displayName: this.$t("Intern"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          name: "Temporary employee",
          displayName: this.$t("Temporary employee"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          name: "No remuneration",
          displayName: this.$t("No remuneration"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }, {
          name: "Freelancer",
          displayName: this.$t("Freelancer"),
          active: false,
          contractType: false,
          saveLeaveAmount: false,
          payPeriod: null,
          calculateBy: null,
          startDayPayPeriod: null,
          shortTimeWorkingEnabled: false,
        }],
        allowanceRules: []
      }
    };
  },
  computed: {
    readOnly() {
      return window.readOnly;
    }
  },
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);
    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;
    }

    if (result.contracts !== null) {
      let contractDecoded;
      try {
        contractDecoded = JSON.parse(result.contracts.replaceAll("&quot;", '"'));
      } catch (e) {
        alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
        this.displayFrontend = true;
        return false;
      }

      this.contract = contractDecoded;
    }

    let language = localStorage.getItem('language');
    if (language === 'nl-NL') {
      this.payrollPeriodUrl = 'https://www.dyflexis.com/wp-content/uploads/2019/04/payrollPeriod_NL.jpg';
    }

    if (language === 'de-DE') {
      this.payrollPeriodUrl = 'https://www.dyflexis.com/wp-content/uploads/2019/04/payrollPeriod_DE.png';
    }

    let valueText;
    for (let i = -28; i <= 28; i++) {
      if (i < 0) {
        valueText = this.$t('{numberOfDays}-days {beforeAfter}', {
          numberOfDays: Math.abs(i),
          beforeAfter: this.$t("before")
        })
        if (i === -1) {
          valueText = this.$t('{numberOfDays}-day {beforeAfter}', {
            numberOfDays: Math.abs(i),
            beforeAfter: this.$t("before")
          })
        }
      }

      if (i === 0) {
        this.monthOptions.push({
          value: 'firstDayMonth',
          text: this.$t("first of the month"),
        });

        continue;
      }

      if (i > 0) {
        valueText = this.$t('{numberOfDays}-days {beforeAfter}', {
          numberOfDays: Math.abs(i),
          beforeAfter: this.$t("after")
        })
        if (i === 1) {
          valueText = this.$t('{numberOfDays}-day {beforeAfter}', {
            numberOfDays: Math.abs(i),
            beforeAfter: this.$t("after")
          })
        }
      }

      this.monthOptions.push({
        value: valueText,
        text: valueText,
      });
    }

    this.contract.contractTypes.forEach(
      contractType => {
        if (contractType.active) {
          this.contractTypesOptions.push({
            value: contractType.displayName,
            text: contractType.displayName,
          });
          this.displayStep = 2;

          if (contractType.displayName === this.$t("Payroll")
            || contractType.displayName === this.$t("Temporary employee")) {
            this.externalContractTypeEnabled = true;
          }

        }

        if (contractType.payPeriod != null) {
          this.displayStep = 3;
        }
      }
    );

    if (this.contract.minMaxDescription != null) {
      this.minMaxEnabled = true;
    }

    if (this.contract.jusDescription) {
      this.jusEnabled = true;
    }

    if (this.contract.jusDescription != null || this.contract.holidayHours != null || this.contract.minMaxDescription != null) {
      this.displayStep = 4;
    }

    GlobalFunctions.setReadOnly(result.isSigned);

    localStorage.setItem('startingObject', JSON.stringify(this.contract));
    this.displayFrontend = true;
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.contract, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  methods: {
    fixedContractBase() {
      this.contract.contractTypes.forEach(
        contractType => {
          if (contractType.active && contractType.contractType) {
            return true;
          }
        }
      );
      return false;
    },
    async handleSave(saveInitialObject) {
      this.safeFreeze = true;
      const result = await SystemRequestService.updateProject(this.contract, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.contract));
      }

      this.safeFreeze = false;
      this.showSaveStatus = true;
      setTimeout(() => {
        this.showSaveStatus = false;
      }, 3000);
    },
    calculateRelativeHoliday() {
      this.contract.holidayPercentage = (this.contract.fulltimeHours / this.contract.holidayHoursYear / 52 * 100).toFixed(5);
    },
    updateOvertimeValues() {
      this.contractTypesOptions.splice(2, 99);

      this.externalContractTypeEnabled = false;
      this.contract.contractTypes.forEach(
        contractType => {
          if (contractType.active) {
            this.contractTypesOptions.push({
              value: contractType.displayName,
              text: contractType.displayName,
            });
          }

          if ((contractType.displayName === this.$t("Payroll")
            || contractType.displayName === this.$t("Temporary employee")) && contractType.active) {
            this.externalContractTypeEnabled = true;
          }
        }
      );
    },
    nextStep(step) {
      if (step > this.displayStep) {
        this.displayStep = step;
      }
    },
    deleteRow(index) {
      this.contract.allowanceRules.splice(index, 1);
    },
    addContractType() {
      if (this.nextContractTypeName === '' || this.nextContractTypeName === null) {
        return;
      }

      this.contract.contractTypes.push({
        name: this.nextContractTypeName,
        displayName: this.nextContractTypeName,
        active: true,
        contractType: false,
        saveLeaveAmount: false,
        payPeriod: null,
        calculateBy: null,
        startDayPayPeriod: null,
        shortTimeWorkingEnabled: false,
      });

      this.nextContractTypeName = null;
    },
  }
};
</script>
